import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import ReactGoogleAutocomplete from "react-google-autocomplete";
import { GOOGLE_API_KEY } from "../../config";
import {
  GOOGLE_PLACE,
  VALIDATIONS_ERROR_MESSAGES,
  capitalizeFirstLetter,
  get_cookie,
  capitalizeFirstWord,
} from "utils";
import { useDispatch, useSelector } from "react-redux";

//Internal
import tooltip from "../../public/assets/images/tooltip.svg";
import { googleDistanceMatrixData } from "store/actions";
import DeliveryInfoModal from "components/modals/deliveryInfoModal";

/*******************
  @Purpose :  Used for pickup address 
  @Parameter : {formik,homeAddress,currentLocationStateName,state,countryOptions,setStateShortName,setCurrentLocationStateName}
  @Author : INIC
  *******************/
const PickupAddress = ({
  formik,
  homeAddress,
  currentLocationStateName,
  state,
  countryOptions,
  setStateShortName,
  setCurrentLocationStateName,
  globalSetting,
  vehicalDetailData,
  setDeliveryFee,
  setReturnFee,
  deliveryFee,
  returnFee,
  setDeliveryMiles,
  setReturnMiles,
  setDropoffStateName,
  setDropoffStateShortName,
}) => {
  const addressInfo = useSelector((state) => state?.common?.addressInfo);
  const { locations } = useSelector(({ common }) => common);
  const cityStateSlug = get_cookie("cityStateSlug") || "";
  const selectedLocation = locations?.find(
    (loc) =>
      `${loc.city_short_name?.toLowerCase()}_${loc.state_short_name?.toLowerCase()}` ===
      cityStateSlug.toLowerCase()
  );
  const isMiami = selectedLocation?.city_short_name?.toLowerCase() === "miami";
  const dispatch = useDispatch();
  const data = get_cookie("selectedCityName");
  const originAddress = globalSetting?.company_address;
  const [searchAddress, setSearchAddress] = useState([]);
  const [showModal, setShowModal] = useState();
  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);
  let dataArray = [];

  // Function to add new address object to the array
  function addAddress(destination, price, miles) {
    let newObj = {
      address: destination,
      price: price,
      miles: miles,
    };
    dataArray.push(newObj);
    setSearchAddress(dataArray);
  }

  const checkboxElement = document.getElementById("one_way");

  // Define a variable to store the current value of one_way_option
  let currentOneWayOption = formik?.values?.one_way_option;

  // Event listener for checkbox click event
  checkboxElement?.addEventListener("click", () => {
    // Update the current value of one_way_option when the checkbox is clicked
    currentOneWayOption = checkboxElement.checked;
  });

  const deliveryFess = (deliveryFees, distance, destination) => {
    let delivery_Fee = false;
    for (let range of deliveryFees) {
      if (distance >= range.from && distance <= range.to) {
        if (formik?.values?.pickup_option && currentOneWayOption) {
          setDeliveryFee(parseFloat(range?.price));
        } else if (formik?.values?.pickup_option && !currentOneWayOption) {
          setDeliveryFee(parseFloat(range?.price * 2));
        }
        delivery_Fee = true;
        break;
      }
    }

    if (!delivery_Fee) {
      setDeliveryFee("");
    }
  };

  const returnFees = (deliveryFees, distance, destination) => {
    let feeSet = false;

    for (let range of deliveryFees) {
      if (distance >= range.from && distance <= range.to) {
        addAddress(destination, parseFloat(range?.price), distance);
        if (formik?.values?.pickup_option && formik?.values?.one_way_option) {
          setReturnFee(parseFloat(range?.price));
        } else {
          setReturnFee(parseFloat(range?.price));
        }
        feeSet = true;
        break;
      }
    }

    if (!feeSet) {
      setReturnFee("");
      // addAddress(destination, "", distance);
    }
  };

  const findPrice = async (destination, isStartAddress = false, postcode) => {
    const deliveryFees =
      vehicalDetailData?.delivery_fees &&
      JSON.parse(vehicalDetailData?.delivery_fees);
    const data = {
      origin: originAddress,
      destination: destination,
    };
    const match = deliveryFees?.find((item) => {
      if (isStartAddress && postcode && item.zipcode == postcode) {
        return true;
      } else if (!isStartAddress && postcode && item.zipcode === postcode) {
        return true;
      }
      return false;
    });
    if (match) {
      if (isStartAddress) {
        setDeliveryFee(
          currentOneWayOption
            ? parseFloat(match.price)
            : parseFloat(match.price * 2)
        );
      }
      if (!isStartAddress) {
        setReturnFee(parseFloat(match.price));
      }
    } else {
      return await dispatch(googleDistanceMatrixData(data))
        .then((res) => {
          if (res?.rows?.length > 0 && res.status === "OK") {
            let distance;
            const distanceInStr = res?.rows?.[0]?.elements?.[0]?.distance?.text;
            if (distanceInStr) {
              distance = Math.round(
                parseFloat(distanceInStr.replace(/[^\d.]/g, ""))
              );
              if (isStartAddress) setDeliveryMiles(distance);
              if (!isStartAddress) setReturnMiles(distance);
              if (isStartAddress) {
                deliveryFess(deliveryFees, distance, destination);
              } else {
                returnFees(deliveryFees, distance, destination);
              }
            } else {
              if (isStartAddress) {
                setDeliveryFee("");
              } else {
                setReturnFee("");
              }
            }
          } else {
            if (isStartAddress) {
              setDeliveryFee("");
            } else {
              setReturnFee("");
            }
          }
        })
        .catch((err) => {
          if (isStartAddress) {
            setDeliveryFee("");
          } else {
            setReturnFee("");
          }
        });
    }
  };

  // For Miami, Delivery is the only available option — auto-select it on mount
  useEffect(() => {
    if (isMiami && !formik.values.pickup_option) {
      formik.setFieldValue("pickup_option", true);
      formik.setFieldValue("is_pickup_selected", false);
    }
  }, [isMiami]);

  /*******************
  @purpose : delivery/return address text can arrive already filled in on
             mount (carried over from the search-results toolbar's location
             draft), but deliveryFee/returnFee are only ever computed inside
             onPlaceSelected/onChange - so without this, a carried-over
             address would sit there with no fee calculated until the
             customer re-touches the field. Runs once, since findPrice
             re-runs itself on every subsequent user edit anyway.
  @Author : INIC
  ******************/
  const hasAutoCalculatedFees = useRef(false);
  useEffect(() => {
    if (hasAutoCalculatedFees.current || !originAddress) return;
    hasAutoCalculatedFees.current = true;

    if (formik.values.pickup_option && formik.values.start_address && deliveryFee == null) {
      findPrice(formik.values.start_address, true);
    }
    if (formik.values.one_way_option && formik.values.return_address && returnFee == null) {
      findPrice(formik.values.return_address, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originAddress]);

  useEffect(() => {
    if (!formik?.values?.start_address) {
      formik.setFieldValue("address", "");
      formik.setFieldValue("city", "");
      formik.setFieldValue("zipcode", "");
      formik.setFieldValue("country", "");
      formik.setFieldValue("state", "");
    } else if (!formik?.values?.return_address) {
      formik.setFieldValue("dropoff_address", "");
      formik.setFieldValue("dropoff_city", "");
      formik.setFieldValue("dropoff_zipcode", "");
      formik.setFieldValue("dropoff_country", "");
      formik.setFieldValue("dropoff_state", "");
    }
  }, [formik?.values?.return_address, formik?.values?.start_address]);
  /*******************
  @Purpose : Google API Hook to handle place search
   @Parameter : {}
   @Author : INIC
 *******************/
  const onPlaceSelected = async (place) => {
    await formik.setFieldTouched("address", true);
    let address = "";
    let sublocality = "";
    let postcode, city, short_name;
    if (place.address_components) {
      for (const component of place.address_components) {
        const componentType = component?.types[0];
        switch (componentType) {
          case "plus_code":
            address = component.short_name + " ";
            await formik.setFieldValue("address", address);
            break;

          case "sublocality_level_1":
            address = component.short_name + " ";
            await formik.setFieldValue("address", address);
            break;

          case "street_number":
            address = component.short_name + " ";
            await formik.setFieldValue("address", address);
            break;

          case "route":
            address += component.short_name;
            await formik.setFieldValue("address", address);
            break;

          case "postal_code": {
            postcode = component.long_name;
            await formik.setFieldValue("zipcode", postcode);
            break;
          }
          case "locality":
            city = component.long_name;
            if (!address) {
              address = city;
              await formik.setFieldValue("address", city);
            }
            await formik.setFieldValue("city", city);
            break;

          case "administrative_area_level_1": {
            short_name = component.short_name;
            await setStateShortName(short_name);

            let stateName = component.long_name;
            await setCurrentLocationStateName(stateName);
            break;
          }
        }
        if (
          (componentType === "sublocality_level_1" ||
            componentType === "postal_town") &&
          componentType !== "locality"
        ) {
          sublocality = component.long_name;

          if (!city) {
            city = sublocality;
            await formik.setFieldValue("city", city);
          }
        }
      }
      let start_address = address;
      if (city && start_address !== city) {
        start_address = start_address + ", " + city;
      }
      if (short_name) {
        start_address = start_address + ", " + short_name;
      }
      if (postcode) {
        start_address = start_address + " " + postcode;
      }
      await formik.setFieldValue("start_address", start_address);
      if (address) {
        await formik.setFieldValue("dropoff_option", "delivery");
        await formik.setFieldValue("is_pickup_selected", false);
        await findPrice(start_address, true, postcode);
      }
    }
  };

  /*******************
  @Purpose : Google API Hook to handle place search
   @Parameter : {}
   @Author : INIC
 *******************/
  const onReturnPlaceSelected = async (place) => {
    await formik.setFieldTouched("return_address", true);

    let address = "";
    let sublocality = "";
    let postcode, city, short_name;
    if (place.address_components) {
      for (const component of place.address_components) {
        const componentType = component?.types[0];
        switch (componentType) {
          case "plus_code":
            address = component.short_name + " ";
            await formik.setFieldValue("dropoff_address", address);
            break;

          case "sublocality_level_1":
            address = component.short_name + " ";
            await formik.setFieldValue("dropoff_address", address);
            break;

          case "street_number":
            address = component.short_name + " ";
            await formik.setFieldValue("dropoff_address", address);
            break;

          case "route":
            address += component.short_name;
            await formik.setFieldValue("dropoff_address", address);
            break;

          case "postal_code": {
            postcode = component.long_name;
            await formik.setFieldValue("dropoff_zipcode", postcode);
            break;
          }
          case "locality":
            city = component.long_name;
            if (!address) {
              address = city;
              await formik.setFieldValue("dropoff_address", city);
            }
            await formik.setFieldValue("dropoff_city", city);
            break;

          case "administrative_area_level_1": {
            short_name = component.short_name;
            await setDropoffStateShortName(short_name);

            let stateName = component.long_name;
            await setDropoffStateName(stateName);
            break;
          }
        }
        if (
          (componentType === "sublocality_level_1" ||
            componentType === "postal_town") &&
          componentType !== "locality"
        ) {
          sublocality = component.long_name;

          if (!city) {
            city = sublocality;
            await formik.setFieldValue("dropoff_city", city);
          }
        }
      }
      let return_address = address;
      if (city) {
        return_address = return_address + ", " + city;
      }
      if (short_name) {
        return_address = return_address + ", " + short_name;
      }
      if (postcode) {
        return_address = return_address + " " + postcode;
      }
      await formik.setFieldValue("return_address", return_address);

      if (return_address) {
        await formik.setFieldValue("dropoff_option", "delivery");
        // await formik.setFieldValue("is_pickup_selected", false);
        await findPrice(return_address, false, postcode);
      }
    }
  };
  return (
    <>
      <>
        <div className="d-flex justify-content-between flex-sm-row pickup-delivery">
          <div className="d-flex return-address tabs-container">
            {!isMiami && (
              <div className="form-check custom-radio tab-radio pl-0 ">
                <input
                  className="form-check-input tab-check-input"
                  type="radio"
                  name="is_pickup_selected"
                  id="pickup_option_office"
                  checked={formik.values?.is_pickup_selected}
                  value={formik.values?.is_pickup_selected}
                  onChange={(e) => {
                    formik.setFieldValue("is_pickup_selected", true);
                    formik.setFieldValue("start_address", "", false);
                    formik.setFieldValue("address", "", false);
                    formik.setFieldValue("pickup_option", false);
                    // formik.setFieldTouched("start_address", false);
                    // formik.setFieldTouched("return_address", false);
                    setDeliveryFee("");
                    if (!formik?.values?.one_way_option) {
                      formik.setFieldValue("dropoff_address", "", false);
                      formik?.setFieldValue("return_address", "", false);
                      formik?.setFieldValue("dropoff_city", "", false);
                      formik?.setFieldValue("dropoffStateShortName", "", false);
                      formik?.setFieldValue("dropoff_zipcode", "", false);
                    }
                  }}
                />
                <span></span>
                <label
                  className="form-check-label tab-label"
                  htmlFor="pickup_option_office"
                >
                  Pickup
                </label>
              </div>
            )}
            {/* <label for="pickup_option_address" class="tab-label">
                    <input type="radio" name="pickup_option" id="pickup_option_address" value="true" checked={formik.values["pickup_option"] === true} onChange={() => { formik.setFieldValue("pickup_option", true); }} />
                    Delivery
                    <div class="tooltip-container">
                      <img src={tooltip} alt="Tooltip" />

                    </div>
                  </label> */}
            <div className="form-check custom-radio tab-radio pl-0">
              <input
                className="form-check-input tab-check-input"
                type="radio"
                name="pickup_option"
                id="pickup_option_address"
                value={formik?.values?.pickup_option}
                checked={formik.values["pickup_option"] === true}
                onChange={() => {
                  formik.setFieldValue("pickup_option", true);
                  formik.setFieldValue("is_pickup_selected", false);

                  formik.setFieldTouched("start_address", false);
                  formik.setFieldTouched("return_address", false);

                  setDeliveryFee("");
                  if (!formik?.values?.one_way_option) {
                    setReturnFee("");
                    formik.setFieldValue("return_address", "", false);
                    formik.setFieldValue("dropoff_address", "", false);
                    formik?.setFieldValue("dropoff_city", "", false);
                    formik?.setFieldValue("dropoffStateShortName", "", false);
                    formik?.setFieldValue("dropoff_zipcode", "", false);
                  }
                }}
              />
              <span></span>
              <label
                className="form-check-label tab-label"
                htmlFor="pickup_option_address"
              >
                Delivery
              </label>
            </div>
          </div>

          <div className="form-check d-flex align-items-center custom-checkbox right">
            <label
              className="form-check-label one-way-label mr-1"
              htmlFor="one_way"
            >
              One-way
              <input
                type="checkbox"
                className="mt-0"
                name="cardSave"
                id="one_way"
                checked={formik?.values["one_way_option"]}
                value={formik?.values?.one_way_option}
                onChange={(e) => {
                  setReturnFee("");
                  formik.setFieldValue("one_way_option", e.target?.checked);
                  formik.setFieldValue("return_address", "", false);
                  formik.setFieldValue("dropoff_address", "", false);
                  if (e.target.checked) {
                    formik.setFieldValue("dropoff_option", "delivery");
                    if (deliveryFee > 0) {
                      setDeliveryFee(deliveryFee / 2);
                    } else {
                      setDeliveryFee(deliveryFee); // Explicitly set it to itself if it's 0 or less
                    }
                  } else {
                    formik.setFieldValue("dropoff_option", "", false);
                    if (deliveryFee > 0) {
                      setDeliveryFee(deliveryFee * 2);
                    } else {
                      setDeliveryFee(deliveryFee); // Explicitly set it to itself if it's 0 or less
                    }
                  }
                }}
              />
              <span></span>
            </label>
            <img
              src={tooltip}
              alt="Tooltip"
              onClick={() => handleShowModal()}
              className="cursor-pointer"
            />
          </div>
        </div>
        <div className="booking-filed">
          <Form className="select-date-range">
            {formik.values.pickup_option && (
              <div className="d-flex input-field">
                <div className="floating-form">
                  <Form.Group
                    controlId="address"
                    className="floating-form-group"
                  >
                    <ReactGoogleAutocomplete
                      apiKey={GOOGLE_API_KEY}
                      type="text"
                      onBlur={(e) => {
                        formik?.handleBlur(e);
                      }}
                      className="floating-input form-control del-input"
                      placeholder={capitalizeFirstWord(
                        `${formik.values?.pickup_option &&
                          formik?.values?.one_way_option
                          ? "Delivery Address"
                          : "Delivery & Return Address"
                        }`
                      )}
                      name="start_address"
                      onPlaceSelected={onPlaceSelected}
                      options={{
                        fields: GOOGLE_PLACE.fields,
                        types: GOOGLE_PLACE.types,
                        componentRestrictions: { country: "us" },
                      }}
                      value={formik.values["start_address"]}
                      onChange={(e) => {
                        formik?.handleChange(e);
                        setDeliveryFee(null);
                        if (!formik?.values?.one_way_option) {
                          formik?.setFieldValue("return_address", "");
                          formik?.setFieldValue("dropoff_address", "");
                          formik?.setFieldValue("dropoff_city", "");

                          formik?.setFieldValue("dropoffStateShortName", "");

                          formik?.setFieldValue("dropoff_zipcode", "");
                        }
                      }}
                    />
                    <Form.Label className="floating-label">
                      {`${formik.values?.pickup_option &&
                        formik?.values?.one_way_option
                        ? "Delivery address*"
                        : "Delivery & return address*"
                        }`}
                    </Form.Label>
                  </Form.Group>

                  {formik.touched.start_address &&
                    (formik.errors.address || formik.errors.city) && (
                      <span className="error-msg d-block">
                        {formik.errors.start_address
                          ? formik.errors.start_address
                          : VALIDATIONS_ERROR_MESSAGES.VALID_ADDRESS}
                      </span>
                    )}
                </div>
              </div>
            )}
            {(formik.values.is_pickup_selected ||
              formik.values.pickup_option) &&
              formik.values.one_way_option && (
                <div className="d-flex input-field">
                  <div className="floating-form">
                    <Form.Group
                      controlId="return_address"
                      className="floating-form-group"
                    >
                      <ReactGoogleAutocomplete
                        apiKey={GOOGLE_API_KEY}
                        type="text"
                        onBlur={(e) => {
                          formik?.handleBlur(e);
                        }}
                        className="floating-input form-control del-input"
                        placeholder={capitalizeFirstWord("Return address")}
                        name="return_address"
                        onPlaceSelected={onReturnPlaceSelected}
                        options={{
                          fields: GOOGLE_PLACE.fields,
                          types: GOOGLE_PLACE.types,
                          componentRestrictions: { country: "us" },
                        }}
                        value={formik.values["return_address"]}
                        onChange={(e) => {
                          formik?.handleChange(e);
                          setReturnFee(null);
                          if (!e.target?.value) {
                            formik?.setFieldValue("return_address", "");
                            formik?.setFieldValue("dropoff_address", "");
                            formik?.setFieldValue("dropoff_city", "");

                            formik?.setFieldValue("dropoffStateShortName", "");

                            formik?.setFieldValue("dropoff_zipcode", "");
                          }
                        }}
                      />
                      <Form.Label className="floating-label">
                        Return Address*
                      </Form.Label>
                    </Form.Group>

                    {formik.touched.return_address &&
                      (formik.errors.dropoff_address ||
                        formik?.errors?.city) && (
                        <span className="error-msg d-block">
                          {formik.errors.return_address
                            ? formik.errors.return_address
                            : VALIDATIONS_ERROR_MESSAGES.VALID_ADDRESS}
                        </span>
                      )}
                  </div>
                </div>
              )}

            <div className="d-none input-field">
              <div className="floating-form">
                <Form.Group controlId="city" className="floating-form-group">
                  <Form.Control
                    type="text"
                    className="floating-input"
                    placeholder="Enter City"
                    name="city"
                    onChange={formik?.handleChange}
                    // onBlur={formik?.handleBlur}
                    onBlur={(e) => {
                      formik?.handleBlur(e);
                    }}
                    value={formik?.values["city"]}
                  />
                  <Form.Label className="floating-label">City*</Form.Label>
                </Form.Group>
                {formik.touched.city && formik.errors.city && (
                  <span className="error-msg d-block">
                    {formik.errors?.city}
                  </span>
                )}
              </div>
              <div className="floating-form">
                <Form.Group controlId="zipCode" className="floating-form-group">
                  <Form.Control
                    type="text"
                    className="floating-input"
                    placeholder="Enter Code"
                    name="zipcode"
                    onChange={formik?.handleChange}
                    onBlur={(e) => {
                      formik?.handleBlur(e);
                    }}
                    value={formik?.values["zipcode"]}
                  />
                  <Form.Label className="floating-label">Zip code*</Form.Label>
                </Form.Group>
                {formik.touched.zipcode && formik.errors.zipcode && (
                  <span className="error-msg d-block">
                    {formik.errors?.zipcode}
                  </span>
                )}
              </div>
            </div>

            <div className="d-none input-field">
              <div className="floating-form">
                <Form.Group
                  controlId="state"
                  className="floating-form-group select-option value-selected"
                >
                  <select
                    id="state"
                    name="state"
                    disabled
                    className="floating-input modified-select form-control"
                    value={
                      currentLocationStateName &&
                      capitalizeFirstLetter(currentLocationStateName)
                    }
                  >
                    {state.length > 0 &&
                      state?.map((option, index) => {
                        return (
                          <option
                            key={index}
                            id={option.id}
                            value={option.label}
                            defaultValue={formik.values.state}
                          >
                            {option.label}
                          </option>
                        );
                      })}
                  </select>
                  <Form.Label className="floating-label">State*</Form.Label>
                </Form.Group>
              </div>
              <div className="floating-form">
                <Form.Group
                  controlId="country"
                  className="floating-form-group select-option value-selected"
                >
                  <select
                    id="country"
                    name="country"
                    disabled
                    className="floating-input modified-select form-control"
                    value={"United States"}
                    onChange={(e) => {
                      formik.setValues(
                        {
                          ...values,
                          country: e.target.value,
                          city: "",
                          state: "",
                        },
                        false
                      );
                      setIsoCode(e);
                    }}
                  >
                    {countryOptions?.map((option, index) => {
                      return (
                        <option
                          key={index}
                          id={option.id}
                          value={option.label}
                          data-isocode={option.id}
                        >
                          {option.label}
                        </option>
                      );
                    })}
                  </select>
                  <Form.Label className="floating-label">Country*</Form.Label>
                </Form.Group>
              </div>
            </div>
          </Form>
        </div>
        <DeliveryInfoModal
          showModal={showModal}
          handleClose={handleCloseModal}
        />
      </>
    </>
  );
};

export default PickupAddress;
